import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import {
  Speed as SpeedIcon,
  Memory as MemoryIcon,
  NetworkCheck as NetworkIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { startOfDay, endOfDay } from 'date-fns';
import { useErrorSetter } from '../hooks/useUiMessages';
import { fetchPerformanceAnalytics } from './analyticsApi';
import styles from './Analytics.module.css';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f'];

const PerformanceMonitor = ({ dateRange }) => {
  const setError = useErrorSetter();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState({
    responseTime: [],
    systemMetrics: [],
    errorRates: [],
    throughput: [],
    serverHealth: [],
    apiPerformance: [],
    databaseMetrics: [],
    userExperience: [],
    routeLatency: [],
    topFailingEndpoints: [],
    summary: {
      averageResponseTime: 0,
      p95ResponseTime: 0,
      uptime: 0,
      errorRate: 0,
      requestsPerMinute: 0,
      systemLoad: 0,
      memoryUsage: 0,
      diskUsage: 0,
      activeConnections: 0,
      slowestEndpoints: [],
      apdex: 1.0
    }
  });

  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
      if (!token) {
        setPerformanceData(prev => ({ ...prev, responseTime: [], systemMetrics: [], summary: { averageResponseTime:0, uptime:0, errorRate:0, requestsPerMinute:0 } }));
        return;
      }
      const startDate = startOfDay(dateRange.start).toISOString();
      const endDate = endOfDay(dateRange.end).toISOString();
      const raw = await fetchPerformanceAnalytics(startDate, endDate);
      // Previous period deltas
      let deltas = { averageResponseTime: 0, errorRate: 0, requestsPerMinute: 0 };
      if (dateRange?.start && dateRange?.end) {
        const ms = dateRange.end.getTime() - dateRange.start.getTime();
        const prevEnd = new Date(dateRange.start.getTime() - 1);
        const prevStart = new Date(prevEnd.getTime() - ms);
        const prev = await fetchPerformanceAnalytics(prevStart.toISOString(), prevEnd.toISOString());
        const s1 = raw.summary || {}; const s0 = prev.summary || {};
        const val = (x) => (typeof x === 'number' && !Number.isNaN(x)) ? x : 0;
        const deltaPct = (now, prev) => prev ? ((now - prev) / prev) * 100 : (now ? 100 : 0);
        deltas = {
          averageResponseTime: deltaPct(val(s1.averageResponseTime), val(s0.averageResponseTime)),
          errorRate: deltaPct(val(s1.errorRate), val(s0.errorRate)),
          requestsPerMinute: deltaPct(val(s1.requestsPerMinute), val(s0.requestsPerMinute))
        };
      }
      setPerformanceData({
        responseTime: raw.responseTime || [],
        systemMetrics: raw.systemMetrics || [],
        errorRates: raw.errorRates || [],
        throughput: raw.throughput || [],
        serverHealth: raw.serverHealth || [],
        apiPerformance: raw.apiPerformance || [],
        databaseMetrics: raw.databaseMetrics || [],
        userExperience: raw.userExperience || [],
  routeLatency: raw.routeLatency || [],
  topFailingEndpoints: raw.topFailingEndpoints || [],
  summary: { ...(raw.summary || { averageResponseTime:0, p95ResponseTime:0, uptime:0, errorRate:0, requestsPerMinute:0, systemLoad:0, memoryUsage:0, diskUsage:0, activeConnections:0, slowestEndpoints:[], apdex:1.0 }), deltas }
      });
    } catch (err) {
      console.error('Error fetching performance data:', err);
      setError('Failed to load performance metrics');
    } finally {
      setLoading(false);
    }
  }, [dateRange, setError]);

  useEffect(() => {
    fetchPerformanceData();
  }, [fetchPerformanceData]);

  // Removed mock generator – backend placeholder endpoint with safe defaults.

  // Removed legacy local transformation helpers (server provides shaped data)

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'success';
      case 'normal': return 'info';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const formatDuration = (ms) => {
    if (ms < 1000) return `${Math.round(ms)}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(1)}%`;
  };

  const formatNumber = (num) => {
    if (num == null || num === '' || Number.isNaN(Number(num))) return '0';
    const n = Number(num);
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  if (loading) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading performance data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className={styles.performanceContainer}>
      {/* Summary Cards */}
      <Grid container spacing={3} className={styles.metricsGrid}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <SpeedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Avg Response Time
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {formatDuration(performanceData.summary.averageResponseTime)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <NetworkIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Apdex
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {typeof performanceData.summary.apdex === 'number' ? performanceData.summary.apdex.toFixed(3) : '1.000'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Route Latency Percentiles */}
        <Grid item xs={12} md={12}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Route Latency (p50 / p95 / p99)
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Route</TableCell>
                      <TableCell align="right">p50</TableCell>
                      <TableCell align="right">p95</TableCell>
                      <TableCell align="right">p99</TableCell>
                      <TableCell align="right">Samples</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(performanceData.routeLatency || []).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.route}</TableCell>
                        <TableCell align="right">
                          <Chip label={`${row.p50}ms`} size="small" color={row.p50 > 300 ? 'error' : row.p50 > 150 ? 'warning' : 'success'} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`${row.p95}ms`} size="small" color={row.p95 > 500 ? 'error' : row.p95 > 300 ? 'warning' : 'success'} />
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={`${row.p99}ms`} size="small" color={row.p99 > 800 ? 'error' : row.p99 > 500 ? 'warning' : 'success'} />
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.samples)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <NetworkIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Uptime
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {formatPercentage(performanceData.summary.uptime)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Error Rate
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {formatPercentage(performanceData.summary.errorRate)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card className={styles.metricCard}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <MemoryIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6" color="textSecondary">
                  Requests/Min
                </Typography>
              </Box>
              <Typography variant="h4" className={styles.metricValue}>
                {formatNumber(performanceData.summary.requestsPerMinute)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={3} className={styles.chartsGrid}>
        {/* Response Time Trends */}
        <Grid item xs={12} lg={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Response Time Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={performanceData.responseTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}ms`, 'Response Time']} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="average" 
                    stroke={COLORS[0]} 
                    strokeWidth={2}
                    name="Average"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="p95" 
                    stroke={COLORS[1]} 
                    strokeWidth={2}
                    name="95th Percentile"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="p99" 
                    stroke={COLORS[2]} 
                    strokeWidth={2}
                    name="99th Percentile"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* System Health Status */}
        <Grid item xs={12} lg={4}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                System Health
              </Typography>
              <Box className={styles.healthMetrics}>
                {performanceData.serverHealth.map((metric, index) => (
                  <Box key={index} className={styles.healthMetric}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">{metric.metric}</Typography>
                      <Chip 
                        label={typeof metric.value === 'number' ? `${metric.value}%` : metric.value}
                        color={getStatusColor(metric.status)}
                        size="small"
                      />
                    </Box>
                    {typeof metric.value === 'number' && (
                      <LinearProgress
                        variant="determinate"
                        value={metric.value}
                        className={styles.healthProgress}
                        color={metric.value > metric.threshold ? 'error' : 'primary'}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* System Resource Usage */}
        <Grid item xs={12} lg={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                System Resource Usage
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={performanceData.systemMetrics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="time" />
                  <YAxis />
                  <Tooltip formatter={(value) => [`${value}%`, 'Usage']} />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="cpu" 
                    stackId="1"
                    stroke={COLORS[3]} 
                    fill={COLORS[3]}
                    name="CPU"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="memory" 
                    stackId="2"
                    stroke={COLORS[4]} 
                    fill={COLORS[4]}
                    name="Memory"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="disk" 
                    stackId="3"
                    stroke={COLORS[5]} 
                    fill={COLORS[5]}
                    name="Disk"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Throughput */}
        <Grid item xs={12} lg={6}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Request Throughput
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={performanceData.throughput}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="requests" fill={COLORS[0]} name="Requests" />
                  <Line type="monotone" dataKey="responses" stroke={COLORS[1]} name="Responses" />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* API Performance Table */}
        <Grid item xs={12} md={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                API Endpoint Performance
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Endpoint</TableCell>
                      <TableCell align="right">Avg Response</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell align="right">Errors</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {performanceData.apiPerformance.map((row, index) => (
                      <TableRow key={index}>
                        <TableCell>{row.endpoint}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${row.avgTime}ms`}
                            color={row.avgTime > 300 ? 'error' : row.avgTime > 150 ? 'warning' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{formatNumber(row.requests)}</TableCell>
                        <TableCell align="right">
                          {row.errors > 0 ? (
                            <Chip label={row.errors} color="error" size="small" />
                          ) : (
                            <Chip label="0" color="success" size="small" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Database Metrics */}
        <Grid item xs={12} md={4}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Database Performance
              </Typography>
              <Box className={styles.dbMetrics}>
                {performanceData.databaseMetrics.map((metric, index) => (
                  <Box key={index} className={styles.dbMetric}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{metric.metric}</Typography>
                      <Chip 
                        label={metric.value}
                        color={getStatusColor(metric.status)}
                        size="small"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Failing Endpoints */}
        <Grid item xs={12} md={8}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Top Failing Endpoints
              </Typography>
              <TableContainer component={Paper} className={styles.tableContainer}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Endpoint</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell align="right">Errors</TableCell>
                      <TableCell align="right">Error Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(performanceData.topFailingEndpoints || []).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.endpoint}</TableCell>
                        <TableCell align="right">{formatNumber(row.requests)}</TableCell>
                        <TableCell align="right">{formatNumber(row.errors)}</TableCell>
                        <TableCell align="right">
                          <Chip label={`${row.errorRate}%`} size="small" color={row.errorRate > 10 ? 'error' : row.errorRate > 3 ? 'warning' : 'success'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Error Rate Trends */}
        <Grid item xs={12}>
          <Card className={styles.chartCard}>
            <CardContent>
              <Typography variant="h6" className={styles.chartTitle}>
                Error Rate & User Experience Trends
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={performanceData.errorRates}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="count" fill={COLORS[2]} name="Error Count" />
                  <Line yAxisId="right" type="monotone" dataKey="rate" stroke={COLORS[0]} name="Error Rate %" />
                </ComposedChart>
              </ResponsiveContainer>
              {/* UX lines (separate chart for clean axes) */}
              <Box sx={{ mt: 3 }}>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={performanceData.userExperience}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="ttfb" stroke={COLORS[0]} strokeWidth={2} name="TTFB (ms)" />
                    <Line type="monotone" dataKey="lcp" stroke={COLORS[1]} strokeWidth={2} name="LCP (ms)" />
                    <Line type="monotone" dataKey="loadTime" stroke={COLORS[2]} strokeWidth={2} name="Load (ms)" />
                    <Line type="monotone" dataKey="cls" stroke={COLORS[3]} strokeWidth={2} name="CLS" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Alerts */}
      <Grid container spacing={2} className={styles.alertsGrid}>
        <Grid item xs={12}>
          <Alert severity="info" className={styles.performanceAlert}>
            <Typography variant="h6">Performance Insights</Typography>
            <Typography variant="body2">
              • Average response time is within acceptable limits (&lt; 300ms)
              • System uptime is excellent at {formatPercentage(performanceData.summary.uptime)}
              • Memory usage is approaching 75% - consider optimization
              • 2 slow queries detected in database - review indexing strategy
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </div>
  );
};

export default PerformanceMonitor;
